/**
 * Scoring para instrumentos propios: PCL-10, RFB-SJ (incl. PED) y RA-10.
 * No revelar ponderaciones en UI; solo usarlas para cálculo.
 */

import type { RFBOptionLetter, RA10OptionLetter } from '@/config/instruments';

// ─── PCL-10 ───
export type PCL10BandKey = 'low' | 'adequate' | 'moderate' | 'high';

export const PCL_10_BANDS: { min: number; max: number; key: PCL10BandKey; label: string }[] = [
  { min: 10, max: 19, key: 'low', label: 'Bajo riesgo (alta confiabilidad)' },
  { min: 20, max: 29, key: 'adequate', label: 'Adecuado' },
  { min: 30, max: 39, key: 'moderate', label: 'Riesgo moderado' },
  { min: 40, max: 50, key: 'high', label: 'Riesgo elevado' },
];

const PCL_10_RH: Record<PCL10BandKey, string> = {
  low: 'El candidato muestra bajo nivel de racionalización de conductas transgresoras y adecuado apego funcional a normas laborales.',
  adequate: 'El perfil sugiere confiabilidad laboral adecuada, con flexibilidad situacional limitada. Puede complementarse con entrevista breve según el puesto.',
  moderate: 'Se observan indicadores de riesgo moderado en confiabilidad, principalmente en justificación de transgresión y flexibilidad ante normas. Se recomienda complementar con entrevista estructurada.',
  high: 'El perfil indica alta normalización de transgresión y justificación de conductas no alineadas a normas. Se sugiere cautela en puestos de alta responsabilidad o manejo de recursos y complementar con entrevista.',
};

export function scorePCL10(rawAnswers: number[]): number {
  if (rawAnswers.length !== 10) return 0;
  return rawAnswers.reduce((s, v) => s + (typeof v === 'number' && v >= 1 && v <= 5 ? v : 0), 0);
}

export function getPCL10Band(totalScore: number): { key: PCL10BandKey; label: string } {
  const band = PCL_10_BANDS.find((b) => totalScore >= b.min && totalScore <= b.max);
  if (band) return { key: band.key, label: band.label };
  if (totalScore < 20) return { key: 'low', label: PCL_10_BANDS[0].label };
  return { key: 'high', label: PCL_10_BANDS[3].label };
}

export function getPCL10RhSummary(bandKey: PCL10BandKey): string {
  return PCL_10_RH[bandKey] ?? '';
}

export function getPCL10BandFromZ(z: number): 'low' | 'adequate' | 'moderate' | 'high' {
  if (z < -0.5) return 'low';
  if (z < 0.5) return 'adequate';
  if (z < 1.5) return 'moderate';
  return 'high';
}

// ─── Indicador derivado: Confiabilidad y Ética Profesional (desde PCL-10) ───
export type ConfiabilidadEticaLevel = 'Alta' | 'Adecuada' | 'En observación' | 'Baja';

export interface ConfiabilidadEticaDerived {
  derived: true;
  sourceInstrumentId: 'PCL_10';
  trustPercent: number;
  level: ConfiabilidadEticaLevel;
  text: string;
}

const CONFIABILIDAD_ETICA_TEXTS: Record<ConfiabilidadEticaLevel, string> = {
  Alta: 'Indicador consistente con apego a normas y baja tolerancia a conductas de riesgo.',
  Adecuada:
    'Indicador compatible con confiabilidad funcional; se sugiere validar con referencias y contexto del puesto.',
  'En observación':
    'Se observan señales de flexibilidad ante normas o justificaciones; conviene profundizar en entrevista y verificación.',
  Baja:
    'Indicador asociado a mayor riesgo conductual; recomendable no asignar funciones sensibles sin validaciones adicionales.',
};

export const CONFIABILIDAD_ETICA_DERIVED_NOTE =
  'Indicador derivado del Perfil de Confiabilidad Laboral (PCL-10). No constituye diagnóstico.';

export function computePCL10DerivedConfianza(totalPCL: number): ConfiabilidadEticaDerived {
  const riskScore = Math.max(10, Math.min(50, totalPCL));
  const trustScore = 50 - riskScore;
  const trustPercent = Math.round((trustScore / 40) * 100);
  let level: ConfiabilidadEticaLevel;
  if (trustPercent >= 75) level = 'Alta';
  else if (trustPercent >= 45) level = 'Adecuada';
  else if (trustPercent >= 20) level = 'En observación';
  else level = 'Baja';
  return {
    derived: true,
    sourceInstrumentId: 'PCL_10',
    trustPercent,
    level,
    text: CONFIABILIDAD_ETICA_TEXTS[level],
  };
}

/** trustPercent (0..100) desde totalPCL (10..50). Usado por Criterio Operativo derivado. */
export function getPCL10TrustPercent(totalPCL: number): number {
  const riskScore = Math.max(10, Math.min(50, totalPCL));
  const trustScore = 50 - riskScore;
  return Math.round((trustScore / 40) * 100);
}

// ─── Indicador derivado: Criterio Operativo y Apego a Normas (RFB-SJ + PCL-10) ───
export type CriterioOperativoLevel = 'Alto' | 'Adecuado' | 'En observación' | 'Bajo';

export interface CriterioOperativoNormasDerived {
  derived: true;
  sourceInstrumentIds: ['RFB_SJ_8', 'PCL_10'];
  operationalNormsIndex: number;
  rfbPercent: number;
  trustPercent: number;
  level: CriterioOperativoLevel;
  text: string;
}

const CRITERIO_OPERATIVO_TEXTS: Record<CriterioOperativoLevel, string> = {
  Alto:
    'Indicador consistente con criterio operativo y apego a lineamientos; tiende a decidir con orden y respeto por procedimientos.',
  Adecuado:
    'Indicador compatible con operación bajo normas; conviene reforzar criterios y validación de procedimientos del puesto.',
  'En observación':
    'Se observan áreas de variabilidad en decisiones o apego; recomendable capacitación inicial y supervisión definida.',
  Bajo:
    'Indicador asociado a decisiones inconsistentes y/o flexibilidad ante normas; evitar funciones sensibles sin validaciones y entrenamiento cercano.',
};

export const CRITERIO_OPERATIVO_DERIVED_NOTE =
  'Indicador derivado de Razonamiento Funcional (Juicio Situacional) y Perfil de Confiabilidad Laboral. No constituye diagnóstico.';

/** RFB totalScore (-8..16) → rfbPercent (0..100). */
function rfbTotalToPercent(rfbTotalScore: number): number {
  const normalized = ((rfbTotalScore + 8) / 24) * 100;
  return Math.round(Math.max(0, Math.min(100, normalized)));
}

export function computeCriterioOperativoNormasDerived(
  rfbTotalScore: number,
  totalPCL: number
): CriterioOperativoNormasDerived {
  const rfbPercent = rfbTotalToPercent(rfbTotalScore);
  const trustPercent = getPCL10TrustPercent(totalPCL);
  const operationalNormsIndex = Math.round(0.6 * rfbPercent + 0.4 * trustPercent);
  let level: CriterioOperativoLevel;
  if (operationalNormsIndex >= 80) level = 'Alto';
  else if (operationalNormsIndex >= 60) level = 'Adecuado';
  else if (operationalNormsIndex >= 40) level = 'En observación';
  else level = 'Bajo';
  return {
    derived: true,
    sourceInstrumentIds: ['RFB_SJ_8', 'PCL_10'],
    operationalNormsIndex,
    rfbPercent,
    trustPercent,
    level,
    text: CRITERIO_OPERATIVO_TEXTS[level],
  };
}

// ─── Indicador derivado: Estilo de Toma de Decisiones Laborales (desde RFB-SJ) ───
export type EstiloDecisionesStyle = 'DIRECTO' | 'ANALITICO' | 'CONSULTIVO' | 'EVITATIVO';

const ESTILO_DECISIONES_MAP: Record<number, Record<RFBOptionLetter, EstiloDecisionesStyle>> = {
  0: { A: 'DIRECTO', B: 'ANALITICO', C: 'ANALITICO', D: 'EVITATIVO' },
  1: { A: 'ANALITICO', B: 'DIRECTO', C: 'ANALITICO', D: 'CONSULTIVO' },
  2: { A: 'DIRECTO', B: 'ANALITICO', C: 'CONSULTIVO', D: 'DIRECTO' },
  3: { A: 'DIRECTO', B: 'ANALITICO', C: 'CONSULTIVO', D: 'DIRECTO' },
  4: { A: 'DIRECTO', B: 'ANALITICO', C: 'CONSULTIVO', D: 'DIRECTO' },
  5: { A: 'DIRECTO', B: 'EVITATIVO', C: 'ANALITICO', D: 'EVITATIVO' },
  6: { A: 'DIRECTO', B: 'ANALITICO', C: 'ANALITICO', D: 'CONSULTIVO' },
  7: { A: 'DIRECTO', B: 'DIRECTO', C: 'CONSULTIVO', D: 'EVITATIVO' },
};

const ESTILO_DECISIONES_TEXTS: Record<EstiloDecisionesStyle, string> = {
  DIRECTO:
    'Tiende a actuar con rapidez y avanzar con lo disponible. Útil en operación; conviene definir límites y criterios para tareas críticas.',
  ANALITICO:
    'Tiende a priorizar calidad y criterios antes de entregar. Útil para precisión; puede requerir tiempos claros en entornos de urgencia.',
  CONSULTIVO:
    'Tiende a buscar alineación y guía antes de decidir. Útil en equipos; conviene definir niveles de autonomía para evitar demoras.',
  EVITATIVO:
    'Tiende a postergar decisiones ante ambigüedad o presión. Conviene estructura, clarificación de prioridades y seguimiento cercano.',
};

export const ESTILO_DECISIONES_DERIVED_NOTE =
  'Indicador derivado del patrón de respuesta en Juicio Situacional. No constituye diagnóstico.';

export interface EstiloDecisionesDerived {
  derived: true;
  sourceInstrumentId: 'RFB_SJ_8';
  counts: { directoCount: number; analiticoCount: number; consultivoCount: number; evitativoCount: number };
  label: string;
  text: string;
  noteOptional?: string;
}

const ESTILO_TO_COUNT_KEY: Record<EstiloDecisionesStyle, keyof { directoCount: number; analiticoCount: number; consultivoCount: number; evitativoCount: number }> = {
  DIRECTO: 'directoCount',
  ANALITICO: 'analiticoCount',
  CONSULTIVO: 'consultivoCount',
  EVITATIVO: 'evitativoCount',
};

export function computeEstiloDecisionesDerived(
  firstChoices: (RFBOptionLetter | string)[]
): EstiloDecisionesDerived {
  const counts = { directoCount: 0, analiticoCount: 0, consultivoCount: 0, evitativoCount: 0 };
  for (let i = 0; i < 8; i++) {
    const opt = firstChoices[i] as RFBOptionLetter | undefined;
    if (!opt || !['A', 'B', 'C', 'D'].includes(opt)) continue;
    const style = ESTILO_DECISIONES_MAP[i]?.[opt];
    if (style) counts[ESTILO_TO_COUNT_KEY[style]]++;
  }
  const styleKeys: EstiloDecisionesStyle[] = ['DIRECTO', 'ANALITICO', 'CONSULTIVO', 'EVITATIVO'];
  const countKeys = ['directoCount', 'analiticoCount', 'consultivoCount', 'evitativoCount'] as const;
  const sorted = styleKeys
    .map((style, i) => ({ style, count: counts[countKeys[i]] }))
    .sort((a, b) => b.count - a.count);
  const top = sorted[0];
  const second = sorted[1];
  const topStyle = top.style;
  const secondStyle = second?.style ?? topStyle;
  const styleLabel = (s: EstiloDecisionesStyle) => s.charAt(0) + s.slice(1).toLowerCase();
  let label: string;
  if (top.count >= 4 && (top.count - (second?.count ?? 0)) >= 2) {
    label = `Predominante: ${styleLabel(topStyle)}`;
  } else if (top.count >= 3 && second && (top.count - second.count) <= 1) {
    label = `Mixto: ${styleLabel(topStyle)} + ${styleLabel(secondStyle)}`;
  } else {
    label = 'Equilibrado';
  }
  const text =
    label === 'Equilibrado'
      ? 'Se observa distribución equilibrada entre estilos; conviene validar en entrevista.'
      : ESTILO_DECISIONES_TEXTS[topStyle];
  const noteOptional =
    counts.evitativoCount >= 3 ? 'Se observa tendencia a postergar ante ambigüedad.' : undefined;
  return {
    derived: true,
    sourceInstrumentId: 'RFB_SJ_8',
    counts,
    label,
    text,
    noteOptional,
  };
}

// ─── RFB-SJ weights (itemIndex 0..7, option A/B/C/D) ───
const RFB_WEIGHTS: Record<number, Record<RFBOptionLetter, number>> = {
  0: { A: 1, B: 2, C: 2, D: 0 },
  1: { A: 2, B: 1, C: 2, D: 1 },
  2: { A: 0, B: 2, C: 2, D: 1 },
  3: { A: 0, B: 2, C: 1, D: 0 },
  4: { A: 0, B: 2, C: 1, D: 0 },
  5: { A: 2, B: 0, C: 1, D: 0 },
  6: { A: 0, B: 2, C: 2, D: 0 },
  7: { A: 0, B: 1, C: 2, D: 0 },
};

export type RFBBandKey = 'high' | 'adequate' | 'low' | 'very_low';

const RFB_BANDS: { min: number; max: number; key: RFBBandKey; label: string }[] = [
  { min: 13, max: 16, key: 'high', label: 'Alto' },
  { min: 9, max: 12, key: 'adequate', label: 'Adecuado' },
  { min: 5, max: 8, key: 'low', label: 'Bajo' },
  { min: -8, max: 4, key: 'very_low', label: 'Muy bajo' },
];

const RFB_RH: Record<RFBBandKey, string> = {
  high: 'El candidato muestra buen juicio funcional y capacidad para priorizar, resolver ambigüedades y tomar decisiones prácticas en el trabajo.',
  adequate: 'El candidato muestra juicio funcional adecuado para la mayoría de roles, con decisiones generalmente prácticas ante presión o ambigüedad.',
  low: 'Se observan decisiones inconsistentes ante presión o ambigüedad. Puede requerir supervisión y lineamientos claros según el puesto.',
  very_low: 'El perfil sugiere dificultades relevantes de priorización y toma de decisiones prácticas. Se recomienda complementar con entrevista y considerar el nivel de supervisión requerido.',
};

// PED: estilo por ítem x opción
const PED_STYLE_MAP: Record<number, Record<RFBOptionLetter, DecisionStyleKey>> = {
  0: { A: 'autonomiaPractica', B: 'apegoNormativo', C: 'autonomiaPractica', D: 'dependenciaJerarquica' },
  1: { A: 'apegoNormativo', B: 'autonomiaPractica', C: 'autonomiaPractica', D: 'dependenciaJerarquica' },
  2: { A: 'dependenciaJerarquica', B: 'apegoNormativo', C: 'apegoNormativo', D: 'autonomiaPractica' },
  3: { A: 'evitacionRiesgo', B: 'autonomiaPractica', C: 'apegoNormativo', D: 'evitacionRiesgo' },
  4: { A: 'evitacionRiesgo', B: 'autonomiaPractica', C: 'dependenciaJerarquica', D: 'evitacionRiesgo' },
  5: { A: 'autonomiaPractica', B: 'dependenciaJerarquica', C: 'apegoNormativo', D: 'evitacionRiesgo' },
  6: { A: 'evitacionRiesgo', B: 'autonomiaPractica', C: 'apegoNormativo', D: 'dependenciaJerarquica' },
  7: { A: 'evitacionRiesgo', B: 'autonomiaPractica', C: 'apegoNormativo', D: 'dependenciaJerarquica' },
};

export type DecisionStyleKey = 'apegoNormativo' | 'autonomiaPractica' | 'dependenciaJerarquica' | 'evitacionRiesgo';

const DECISION_STYLE_LABELS: Record<DecisionStyleKey, string> = {
  apegoNormativo: 'Apego Normativo',
  autonomiaPractica: 'Autonomía Práctica',
  dependenciaJerarquica: 'Dependencia Jerárquica',
  evitacionRiesgo: 'Evitación del Riesgo',
};

const PED_RH: Record<DecisionStyleKey, string> = {
  autonomiaPractica: 'Estilo predominante: Autonomía práctica. Tiende a resolver y avanzar con criterio operativo. Favorece roles con toma de decisiones en campo o dinámica.',
  apegoNormativo: 'Estilo predominante: Apego normativo. Tiende a seguir procedimientos y buscar alineación con reglas. Favorece entornos regulados y de calidad.',
  dependenciaJerarquica: 'Estilo predominante: Dependencia jerárquica. Tiende a escalar decisiones y requerir instrucciones claras. Favorece roles con supervisión definida.',
  evitacionRiesgo: 'Estilo predominante: Evitación del riesgo. Tiende a minimizar errores y evitar decisiones bajo incertidumbre. Útil en tareas críticas, pero puede enlentecer la ejecución.',
};

const DOMINANT_PRIORITY: DecisionStyleKey[] = ['autonomiaPractica', 'apegoNormativo', 'dependenciaJerarquica', 'evitacionRiesgo'];
const RISK_PRIORITY: DecisionStyleKey[] = ['evitacionRiesgo', 'dependenciaJerarquica', 'apegoNormativo', 'autonomiaPractica'];

export function weightRFB(itemIndex: number, option: RFBOptionLetter): number {
  const row = RFB_WEIGHTS[itemIndex];
  return row ? row[option] ?? 0 : 0;
}

export function scoreRFBSJItem(itemIndex: number, firstChoice: RFBOptionLetter, avoidChoice: RFBOptionLetter): number {
  return weightRFB(itemIndex, firstChoice) - weightRFB(itemIndex, avoidChoice);
}

export function scoreRFBSJTotal(
  firstChoices: RFBOptionLetter[],
  avoidChoices: RFBOptionLetter[]
): { totalScore: number; itemScores: number[] } {
  const itemScores = firstChoices.map((f, i) => scoreRFBSJItem(i, f, avoidChoices[i]));
  const totalScore = itemScores.reduce((s, v) => s + v, 0);
  return { totalScore, itemScores };
}

/**
 * Construye el aggregate RFB_SJ_8 para persistir en results.
 * IMPORTANTE: firstChoices (array de 8) debe persistirse SIEMPRE para permitir
 * derivados (Estilo de Decisiones, Criterio Operativo).
 */
export function buildRFBAggregateForPersistence(
  firstChoices: RFBOptionLetter[],
  avoidChoices: RFBOptionLetter[]
): Record<string, unknown> {
  const { totalScore, itemScores } = scoreRFBSJTotal(firstChoices, avoidChoices);
  const band = getRFBBand(totalScore);
  const decisionStyle = computePED(firstChoices, avoidChoices);
  const estiloDerived = computeEstiloDecisionesDerived(firstChoices);
  return {
    totalScore,
    itemScores,
    bandKey: band.key,
    bandLabel: band.label,
    rhSummary: getRFBRhSummary(band.key),
    firstChoices: firstChoices.slice(0, 8),
    avoidChoices: avoidChoices.slice(0, 8),
    decisionStyle,
    derived: {
      estiloDecisiones: estiloDerived,
    },
  };
}

export function getRFBBand(totalScore: number): { key: RFBBandKey; label: string } {
  const band = RFB_BANDS.find((b) => totalScore >= b.min && totalScore <= b.max);
  if (band) return { key: band.key, label: band.label };
  return { key: 'very_low', label: RFB_BANDS[3].label };
}

export function getRFBRhSummary(bandKey: RFBBandKey): string {
  return RFB_RH[bandKey] ?? '';
}

export interface DecisionStyleResult {
  apegoNormativo: number;
  autonomiaPractica: number;
  dependenciaJerarquica: number;
  evitacionRiesgo: number;
  dominantStyle: DecisionStyleKey;
  secondaryStyle: DecisionStyleKey;
  riskStyle: DecisionStyleKey;
  rhStyleSummary: string;
}

function getStyleScores(
  firstChoices: RFBOptionLetter[],
  avoidChoices: RFBOptionLetter[]
): Record<DecisionStyleKey, number> {
  const scores: Record<DecisionStyleKey, number> = {
    apegoNormativo: 0,
    autonomiaPractica: 0,
    dependenciaJerarquica: 0,
    evitacionRiesgo: 0,
  };
  for (let i = 0; i < 8; i++) {
    const firstStyle = PED_STYLE_MAP[i][firstChoices[i]];
    const avoidStyle = PED_STYLE_MAP[i][avoidChoices[i]];
    if (firstStyle) scores[firstStyle] += 1;
    if (avoidStyle) scores[avoidStyle] -= 1;
  }
  return scores;
}

function pickDominant(scores: Record<DecisionStyleKey, number>, priority: DecisionStyleKey[]): DecisionStyleKey {
  let best: DecisionStyleKey = priority[0];
  let bestScore = scores[best];
  for (const k of priority) {
    if (scores[k] > bestScore) {
      best = k;
      bestScore = scores[k];
    }
  }
  return best;
}

function pickSecondary(scores: Record<DecisionStyleKey, number>, dominant: DecisionStyleKey): DecisionStyleKey {
  const rest = DOMINANT_PRIORITY.filter((k) => k !== dominant);
  let best: DecisionStyleKey = rest[0];
  let bestScore = scores[best];
  for (const k of rest) {
    if (scores[k] > bestScore) {
      best = k;
      bestScore = scores[k];
    }
  }
  return best;
}

function pickRisk(scores: Record<DecisionStyleKey, number>): DecisionStyleKey {
  let worst: DecisionStyleKey = RISK_PRIORITY[0];
  let worstScore = scores[worst];
  for (const k of RISK_PRIORITY) {
    if (scores[k] < worstScore) {
      worst = k;
      worstScore = scores[k];
    }
  }
  return worst;
}

export function computePED(
  firstChoices: RFBOptionLetter[],
  avoidChoices: RFBOptionLetter[]
): DecisionStyleResult {
  const styleScores = getStyleScores(firstChoices, avoidChoices);
  const dominantStyle = pickDominant(styleScores, DOMINANT_PRIORITY);
  const secondaryStyle = pickSecondary(styleScores, dominantStyle);
  const riskStyle = pickRisk(styleScores);
  const rhStyleSummary = PED_RH[dominantStyle] ?? '';
  return {
    apegoNormativo: styleScores.apegoNormativo,
    autonomiaPractica: styleScores.autonomiaPractica,
    dependenciaJerarquica: styleScores.dependenciaJerarquica,
    evitacionRiesgo: styleScores.evitacionRiesgo,
    dominantStyle,
    secondaryStyle,
    riskStyle,
    rhStyleSummary,
  };
}

export function getDecisionStyleLabel(key: string): string {
  return DECISION_STYLE_LABELS[key as DecisionStyleKey] ?? key;
}

// ─── RA-10 (Responsabilidad y Autogestión) ───
const RA10_WEIGHTS: Record<number, Record<RA10OptionLetter, number>> = {
  0: { A: 2, B: 0, C: 1 },
  1: { A: 2, B: 0, C: 1 },
  2: { A: 1, B: 2, C: 0 },
  3: { A: 2, B: 0, C: 1 },
  4: { A: 1, B: 0, C: 2 },
  5: { A: 2, B: 0, C: 1 },
  6: { A: 2, B: 0, C: 1 },
  7: { A: 1, B: 2, C: 0 },
  8: { A: 2, B: 1, C: 0 },
  9: { A: 1, B: 0, C: 2 },
};

export type RA10BandKey = 'desarrollo' | 'media' | 'alta';

const RA10_BANDS: { min20: number; max20: number; min50: number; max50: number; key: RA10BandKey; label: string }[] = [
  { min20: 0, max20: 8, min50: 10, max50: 26, key: 'desarrollo', label: 'Adecuación en Desarrollo' },
  { min20: 9, max20: 14, min50: 28, max50: 38, key: 'media', label: 'Adecuación Media' },
  { min20: 15, max20: 20, min50: 40, max50: 50, key: 'alta', label: 'Adecuación Alta' },
];

const RA10_RH: Record<RA10BandKey, string> = {
  alta: 'Presenta un patrón de autogestión consistente, con buena organización funcional y seguimiento de pendientes en condiciones reales de trabajo. Sugerencia operativa: Puede iniciar con supervisión ligera y metas claras. Recomendable asignar responsabilidades con autonomía progresiva.',
  media: 'Muestra funcionamiento adecuado; tiende a rendir mejor cuando cuenta con estructura, prioridades claras y seguimiento periódico. Sugerencia operativa: Inducción con checklist y criterios de entrega. Revisión breve semanal durante el primer mes.',
  desarrollo: 'Se observan patrones que pueden afectar la constancia, priorización o seguimiento bajo carga. Puede beneficiarse de mayor estructura al inicio. Sugerencia operativa: Supervisión más cercana en inducción y objetivos diarios/semanales. Uso de listas, recordatorios y verificación de cierre de pendientes.',
};

export function weightRA10(itemIndex: number, option: RA10OptionLetter): number {
  const row = RA10_WEIGHTS[itemIndex];
  return row ? row[option] ?? 0 : 0;
}

export function scoreRA10(choices: RA10OptionLetter[]): { score20: number; score50: number; itemScores: number[] } {
  const itemScores = choices.map((c, i) => weightRA10(i, c));
  const score20 = itemScores.reduce((s, v) => s + v, 0);
  const score50 = 10 + score20 * 2;
  return { score20, score50, itemScores };
}

export function getRA10Band(score20: number): { key: RA10BandKey; label: string } {
  const band = RA10_BANDS.find((b) => score20 >= b.min20 && score20 <= b.max20);
  if (band) return { key: band.key, label: band.label };
  return { key: 'desarrollo', label: RA10_BANDS[0].label };
}

export function getRA10RhSummary(bandKey: RA10BandKey): string {
  return RA10_RH[bandKey] ?? '';
}

export function getRA10BandFromZ(z: number): 'desarrollo' | 'media' | 'alta' {
  if (z < -0.5) return 'desarrollo';
  if (z < 0.5) return 'media';
  return 'alta';
}

export const RA_10_DERIVED_NOTE =
  'Indicador derivado del instrumento RA-10. No constituye diagnóstico.';

// ─── EEL-12 (Autogestión Emocional en el Trabajo) ───
/** Índices invertidos (0-based): 4, 7, 10, 12 → 3, 6, 9, 11 */
const EEL_12_INVERTED_INDICES = new Set([3, 6, 9, 11]);

export type EEL12BandKey = 'desarrollo' | 'media' | 'alta';

/** Rangos EEL-12 (score50 10..50). Sin huecos: cubre 27 y 39. */
const EEL12_BANDS: { min50: number; max50: number; key: EEL12BandKey; label: string }[] = [
  { min50: 10, max50: 27, key: 'desarrollo', label: 'Adecuación en Desarrollo' },
  { min50: 28, max50: 39, key: 'media', label: 'Adecuación Media' },
  { min50: 40, max50: 50, key: 'alta', label: 'Adecuación Alta' },
];

const EEL12_RH: Record<EEL12BandKey, string> = {
  alta: 'Presenta buena autogestión emocional en contextos laborales. Mantiene estabilidad, control de impulsos y tolerancia a la frustración incluso bajo presión. Sugerencia operativa: Puede desempeñarse en entornos dinámicos con demandas simultáneas y trato frecuente con personas.',
  media: 'Muestra autogestión emocional funcional. En picos de presión o contratiempos puede verse afectado, pero tiende a recuperar estabilidad con estructura y pausas. Sugerencia operativa: Se recomienda claridad de prioridades, retroalimentación breve y apoyo de organización del trabajo en periodos de alta carga.',
  desarrollo: 'Se observan señales de reactividad o dificultad para sostener estabilidad bajo presión. Esto puede afectar concentración, comunicación o rendimiento en situaciones demandantes. Sugerencia operativa: Se beneficia de lineamientos claros, supervisión inicial más cercana y estrategias de manejo de presión (pausas breves, orden de prioridades y comunicación clara).',
};

const EEL12_CONSISTENCY_LABELS: Record<number, string> = {
  0: 'Adecuada',
  1: 'Variable',
  2: 'Posible respuesta idealizada o inconsistente',
  3: 'Posible respuesta idealizada o inconsistente',
};

const EEL12_CONSISTENCY_NOTE_VARIABLE =
  'Se observó variación poco consistente en algunas respuestas. Se recomienda validar con entrevista breve de confirmación.';
const EEL12_CONSISTENCY_NOTE_IDEALIZED =
  'Se identificaron respuestas con consistencia baja entre reactivos relacionados, lo que puede reducir la precisión del perfil. Se sugiere corroborar con preguntas conductuales y referencias.';

export interface EEL12ConsistencyResult {
  inconsistencyCount: number;
  consistencyLabel: string;
  difA: number;
  difB: number;
  difC: number;
  consistencyNote?: string;
}

function scoreEEL12Item(rawResp: number, index: number): number {
  const clamped = Math.max(1, Math.min(5, rawResp));
  return EEL_12_INVERTED_INDICES.has(index) ? 6 - clamped : clamped;
}

export function scoreEEL12(rawAnswers: number[]): {
  scoreRaw: number;
  score50: number;
  itemScores: number[];
} {
  if (rawAnswers.length !== 12) return { scoreRaw: 0, score50: 10, itemScores: [] };
  const itemScores = rawAnswers.map((r, i) => scoreEEL12Item(r, i));
  const scoreRaw = itemScores.reduce((s, v) => s + v, 0);
  const norm = (scoreRaw - 12) / 48;
  const score50 = Math.round(10 + norm * 40);
  return { scoreRaw, score50, itemScores };
}

export function getEEL12Band(score50: number): { key: EEL12BandKey; label: string } {
  const band = EEL12_BANDS.find((b) => score50 >= b.min50 && score50 <= b.max50);
  if (band) return { key: band.key, label: band.label };
  if (import.meta.env.DEV) {
    console.warn(`[EEL-12] getEEL12Band: score50=${score50} fuera de rango 10..50, usando fallback`);
  }
  if (score50 < 28) return { key: 'desarrollo', label: EEL12_BANDS[0].label };
  if (score50 < 40) return { key: 'media', label: EEL12_BANDS[1].label };
  return { key: 'alta', label: EEL12_BANDS[2].label };
}

export function getEEL12RhSummary(bandKey: EEL12BandKey): string {
  return EEL12_RH[bandKey] ?? '';
}

export function getEEL12BandFromZ(z: number): 'desarrollo' | 'media' | 'alta' {
  if (z < -0.5) return 'desarrollo';
  if (z < 0.5) return 'media';
  return 'alta';
}

/** Par A: ítem 3 (idx 2) POS vs ítem 4 (idx 3) NEG invertido */
/** Par B: ítem 6 (idx 5) POS vs ítem 7 (idx 6) NEG invertido */
/** Par C: ítem 9 (idx 8) POS vs ítem 10 (idx 9) NEG invertido */
// ─── JK_OFFICE_ADMIN (Conocimiento Oficina administrativa) ───
export type JKOfficeAdminBandKey = 'desarrollo' | 'media' | 'alta';

const JK_OA_BANDS: { min: number; max: number; key: JKOfficeAdminBandKey; label: string }[] = [
  { min: 0, max: 3, key: 'desarrollo', label: 'En desarrollo' },
  { min: 4, max: 7, key: 'media', label: 'Media' },
  { min: 8, max: 10, key: 'alta', label: 'Alta' },
];

export interface JKOfficeAdminScoreResult {
  correctCount: number;
  scorePercent: number;
  answeredNoSeCount: number;
  bandKey: JKOfficeAdminBandKey;
  bandLabel: string;
}

export function scoreJKOfficeAdmin(
  itemsPresented: { id: string; correctAnswer: 'V' | 'F' }[],
  answers: Record<string, 'V' | 'F' | 'N'>
): JKOfficeAdminScoreResult {
  let correctCount = 0;
  let answeredNoSeCount = 0;
  for (const item of itemsPresented) {
    const ans = answers[item.id];
    if (ans === 'N') answeredNoSeCount += 1;
    else if (ans === item.correctAnswer) correctCount += 1;
  }
  const total = itemsPresented.length;
  const scorePercent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const band = JK_OA_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  const bandKey = band?.key ?? 'desarrollo';
  const bandLabel = band?.label ?? JK_OA_BANDS[0].label;
  return { correctCount, scorePercent, answeredNoSeCount, bandKey, bandLabel };
}

export function getJKOfficeAdminBand(correctCount: number): { key: JKOfficeAdminBandKey; label: string } {
  const band = JK_OA_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  return band ? { key: band.key, label: band.label } : { key: 'desarrollo', label: JK_OA_BANDS[0].label };
}

// ─── JK_SALES_RETAIL (Conocimiento Ventas / vendedor de piso) ───
export type JKSalesRetailBandKey = 'desarrollo' | 'media' | 'alta';

const JK_SR_BANDS: { min: number; max: number; key: JKSalesRetailBandKey; label: string }[] = [
  { min: 0, max: 3, key: 'desarrollo', label: 'Adecuación en Desarrollo' },
  { min: 4, max: 7, key: 'media', label: 'Adecuación Media' },
  { min: 8, max: 10, key: 'alta', label: 'Adecuación Alta' },
];

export interface JKSalesRetailScoreResult {
  correctCount: number;
  scorePercent: number;
  answeredNoSeCount: number;
  bandKey: JKSalesRetailBandKey;
  bandLabel: string;
}

export function scoreJKSalesRetail(
  itemsPresented: { id: string; correctAnswer: 'V' | 'F' }[],
  answers: Record<string, 'V' | 'F' | 'N'>
): JKSalesRetailScoreResult {
  let correctCount = 0;
  let answeredNoSeCount = 0;
  for (const item of itemsPresented) {
    const ans = answers[item.id];
    if (ans === 'N') answeredNoSeCount += 1;
    else if (ans === item.correctAnswer) correctCount += 1;
  }
  const total = itemsPresented.length;
  const scorePercent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const band = JK_SR_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  const bandKey = band?.key ?? 'desarrollo';
  const bandLabel = band?.label ?? JK_SR_BANDS[0].label;
  return { correctCount, scorePercent, answeredNoSeCount, bandKey, bandLabel };
}

export function getJKSalesRetailBand(correctCount: number): { key: JKSalesRetailBandKey; label: string } {
  const band = JK_SR_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  return band ? { key: band.key, label: band.label } : { key: 'desarrollo', label: JK_SR_BANDS[0].label };
}

// ─── APO-6 (Atención y Precisión Operativa) ───
export type APO6BandKey = 'desarrollo' | 'media' | 'alta';

const APO6_BANDS: { min: number; max: number; key: APO6BandKey; label: string }[] = [
  { min: 0, max: 2, key: 'desarrollo', label: 'En desarrollo' },
  { min: 3, max: 4, key: 'media', label: 'Media' },
  { min: 5, max: 6, key: 'alta', label: 'Alta' },
];

export const APO6_NOTE =
  'Indicador de precisión operativa; no sustituye evaluación técnica del puesto.';

export interface APO6ScoreResult {
  correctCount: number;
  percent: number;
  adecuacion: APO6BandKey;
  adecuacionLabel: string;
}

export function scoreAPO6(
  choices: (string | undefined)[],
  correctAnswers: (string | undefined)[]
): APO6ScoreResult {
  let correctCount = 0;
  for (let i = 0; i < 6; i++) {
    if (choices[i] === correctAnswers[i]) correctCount += 1;
  }
  const percent = Math.round((correctCount / 6) * 100);
  const band = APO6_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  const adecuacion = (band?.key ?? 'desarrollo') as APO6BandKey;
  const adecuacionLabel = band?.label ?? APO6_BANDS[0].label;
  return { correctCount, percent, adecuacion, adecuacionLabel };
}

export function getAPO6Band(correctCount: number): { key: APO6BandKey; label: string } {
  const band = APO6_BANDS.find((b) => correctCount >= b.min && correctCount <= b.max);
  return band ? { key: band.key, label: band.label } : { key: 'desarrollo', label: APO6_BANDS[0].label };
}

// ─── ML-8 (Motivadores Laborales, elección forzada A/B) ───
export type ML8OptionLetter = 'A' | 'B';

const ML8_DERIVED_NOTE =
  'Indicador derivado a partir de preferencias motivacionales. No es una medida directa de desempeño.';

const PERSISTENCIA_TEXTS: Record<string, string> = {
  alto:
    'Perfil motivacional orientado a retos y resultados; suele sostener esfuerzo cuando hay metas claras y margen de decisión.',
  adecuado:
    'Puede responder bien a metas y presión moderada cuando hay claridad de objetivos y seguimiento.',
  en_desarrollo:
    'Puede preferir contextos con menor presión por resultado o metas graduales; conviene acompañar con objetivos claros y apoyo inicial.',
};

const INTERACCION_TEXTS: Record<string, string> = {
  alto:
    'Preferencia motivacional compatible con entornos colaborativos, con lineamientos claros y procesos definidos de interacción.',
  adecuado:
    'Puede adaptarse a interacción profesional si hay criterios claros de comunicación y un marco de trabajo consistente.',
  en_desarrollo:
    'Preferencia motivacional menos orientada a estructura y colaboración; conviene clarificar expectativas de trato y lineamientos de interacción.',
};

export interface ML8DerivedResult {
  score: number;
  level: 'en_desarrollo' | 'adecuado' | 'alto';
  text: string;
}

export interface ML8ScoreResult {
  rawAnswers: ML8OptionLetter[];
  counts: {
    Acount1: number;
    Bcount1: number;
    Acount2: number;
    Bcount2: number;
    Acount3: number;
    Bcount3: number;
    Acount4: number;
    Bcount4: number;
  };
  labels: {
    label_dim1: string;
    label_dim2: string;
    label_dim3: string;
    label_dim4: string;
  };
  derived: {
    persistenciaPresion: ML8DerivedResult;
    interaccionProfesional: ML8DerivedResult;
  };
}

function getML8DimLabel(
  aCount: number,
  bCount: number,
  poleA: string,
  poleB: string
): string {
  if (bCount === 2) return `Preferencia marcada por ${poleB}`;
  if (aCount === 2) return `Preferencia marcada por ${poleA}`;
  return 'Preferencia equilibrada';
}

const ML8_LEVEL_LABELS: Record<string, string> = {
  en_desarrollo: 'En desarrollo',
  adecuado: 'Adecuado',
  alto: 'Alto',
};

function getDerivedLevel(score: number): 'en_desarrollo' | 'adecuado' | 'alto' {
  if (score >= 5) return 'alto';
  if (score >= 3) return 'adecuado';
  return 'en_desarrollo';
}

export function getML8LevelLabel(level: string): string {
  return ML8_LEVEL_LABELS[level] ?? level;
}

export function scoreML8(rawAnswers: ML8OptionLetter[]): ML8ScoreResult {
  if (rawAnswers.length !== 8) {
    if (import.meta.env.DEV) {
      console.warn('[ML-8] scoreML8: se requieren 8 respuestas, recibidas', rawAnswers.length);
    }
    const pad = [...rawAnswers];
    while (pad.length < 8) pad.push('A');
    rawAnswers = pad.slice(0, 8) as ML8OptionLetter[];
  }
  const Acount1 = [rawAnswers[0], rawAnswers[1]].filter((x) => x === 'A').length;
  const Bcount1 = [rawAnswers[0], rawAnswers[1]].filter((x) => x === 'B').length;
  const Acount2 = [rawAnswers[2], rawAnswers[3]].filter((x) => x === 'A').length;
  const Bcount2 = [rawAnswers[2], rawAnswers[3]].filter((x) => x === 'B').length;
  const Acount3 = [rawAnswers[4], rawAnswers[5]].filter((x) => x === 'A').length;
  const Bcount3 = [rawAnswers[4], rawAnswers[5]].filter((x) => x === 'B').length;
  const Acount4 = [rawAnswers[6], rawAnswers[7]].filter((x) => x === 'A').length;
  const Bcount4 = [rawAnswers[6], rawAnswers[7]].filter((x) => x === 'B').length;

  const counts = {
    Acount1,
    Bcount1,
    Acount2,
    Bcount2,
    Acount3,
    Bcount3,
    Acount4,
    Bcount4,
  };

  const labels = {
    label_dim1: getML8DimLabel(Acount1, Bcount1, 'Estabilidad', 'Crecimiento'),
    label_dim2: getML8DimLabel(Acount2, Bcount2, 'Compensación', 'Ambiente'),
    label_dim3: getML8DimLabel(Acount3, Bcount3, 'Autonomía', 'Guía'),
    label_dim4: getML8DimLabel(Acount4, Bcount4, 'Rutina', 'Variedad'),
  };

  // Derivado A: Persistencia y Desempeño bajo Presión = Bcount1 + Acount2 + Acount3 (0..6)
  const scoreA = Bcount1 + Acount2 + Acount3;
  const levelA = getDerivedLevel(scoreA);
  const persistenciaPresion: ML8DerivedResult = {
    score: scoreA,
    level: levelA,
    text: PERSISTENCIA_TEXTS[levelA] ?? PERSISTENCIA_TEXTS.en_desarrollo,
  };

  // Derivado B: Competencias de Interacción Profesional = Bcount2 + Bcount3 + Acount4 (0..6)
  const scoreB = Bcount2 + Bcount3 + Acount4;
  const levelB = getDerivedLevel(scoreB);
  const interaccionProfesional: ML8DerivedResult = {
    score: scoreB,
    level: levelB,
    text: INTERACCION_TEXTS[levelB] ?? INTERACCION_TEXTS.en_desarrollo,
  };

  return {
    rawAnswers: rawAnswers.slice(0, 8),
    counts,
    labels,
    derived: { persistenciaPresion, interaccionProfesional },
  };
}

export const ML8_DERIVED_NOTE_EXPORT = ML8_DERIVED_NOTE;

export function computeEEL12Consistency(rawAnswers: number[]): EEL12ConsistencyResult {
  const dif = (posIdx: number, negIdx: number) => {
    const respPos = rawAnswers[posIdx] ?? 0;
    const respNeg = rawAnswers[negIdx] ?? 0;
    const negCorr = 6 - Math.max(1, Math.min(5, respNeg));
    return Math.abs(respPos - negCorr);
  };
  const difA = dif(2, 3);
  const difB = dif(5, 6);
  const difC = dif(8, 9);
  const inconsistentA = difA >= 3;
  const inconsistentB = difB >= 3;
  const inconsistentC = difC >= 3;
  const inconsistencyCount = [inconsistentA, inconsistentB, inconsistentC].filter(Boolean).length;
  const consistencyLabel = EEL12_CONSISTENCY_LABELS[inconsistencyCount] ?? 'Adecuada';
  let consistencyNote: string | undefined;
  if (inconsistencyCount === 1) consistencyNote = EEL12_CONSISTENCY_NOTE_VARIABLE;
  else if (inconsistencyCount >= 2) consistencyNote = EEL12_CONSISTENCY_NOTE_IDEALIZED;
  return { inconsistencyCount, consistencyLabel, difA, difB, difC, consistencyNote };
}

/**
 * Pruebas mínimas de getEEL12Band (sin framework de tests).
 * Ejecutar en consola: import { runEEL12BandTests } from '@/lib/instrumentScoring'; runEEL12BandTests()
 */
export function runEEL12BandTests(): boolean {
  const cases: { score50: number; expected: EEL12BandKey }[] = [
    { score50: 10, expected: 'desarrollo' },
    { score50: 27, expected: 'desarrollo' },
    { score50: 28, expected: 'media' },
    { score50: 39, expected: 'media' },
    { score50: 40, expected: 'alta' },
    { score50: 50, expected: 'alta' },
  ];
  let ok = true;
  for (const { score50, expected } of cases) {
    const { key } = getEEL12Band(score50);
    if (key !== expected) {
      console.error(`[EEL-12] getEEL12Band(${score50}): esperado "${expected}", obtuvo "${key}"`);
      ok = false;
    }
  }
  if (ok) console.log('[EEL-12] runEEL12BandTests: OK');
  return ok;
}

// ─── Mini-IPIP (20) — Perfil Integral de Personalidad Profesional ───
const MINI_IPIP_INV_INDICES = new Set([1, 5, 6, 7, 8, 9, 15, 16, 17, 18, 19]);

function invertMiniIPIP(x: number): number {
  const v = typeof x === 'number' && x >= 1 && x <= 5 ? x : 3;
  return 6 - v;
}

export type MiniIPIPLevel = 'Bajo' | 'Promedio' | 'Alto';

export interface MiniIPIPDimensionResult {
  avg: number;
  percent: number;
  level: MiniIPIPLevel;
  text: string;
  z?: number;
  normUsed?: string;
  nNorm?: number;
}

/** Textos RH: Bajo/Promedio/Alto mapean a los textos originales En desarrollo/Adecuado/Alto */
const MINI_IPIP_TEXTS: Record<string, Record<MiniIPIPLevel, string>> = {
  responsabilidadOrden: {
    Bajo: 'Puede requerir estructura, recordatorios y supervisión inicial.',
    Promedio: 'Organización funcional; se beneficia de metas claras.',
    Alto: 'Tendencia a organización, seguimiento y cierre de tareas.',
  },
  estabilidadEmocional: {
    Bajo: 'Puede reaccionar con tensión ante presión; conviene acompañamiento y estructura.',
    Promedio: 'Manejo funcional del estrés; conviene claridad y prioridades.',
    Alto: 'Suele mantener estabilidad ante presión y recuperarse de contratiempos.',
  },
  energiaSocial: {
    Bajo: 'Prefiere baja exposición social; conviene roles con interacción acotada o lineamientos claros.',
    Promedio: 'Interacción funcional; se adapta según contexto del rol.',
    Alto: 'Tiende a participar, comunicarse e involucrarse con el equipo.',
  },
  cooperacion: {
    Bajo: 'Puede mostrar fricción o impaciencia; conviene reglas de comunicación y feedback.',
    Promedio: 'Colaboración funcional; se beneficia de acuerdos claros.',
    Alto: 'Tiende a cooperar, cuidar el trato y reducir fricción interpersonal.',
  },
  aperturaAdaptabilidad: {
    Bajo: 'Prefiere lo conocido; conviene capacitación gradual y procesos claros.',
    Promedio: 'Flexibilidad funcional; se adapta con guía.',
    Alto: 'Tiende a aprender y adaptarse; propone mejoras con facilidad.',
  },
};

function getMiniIPIPLevelFromZ(z: number): MiniIPIPLevel {
  if (z <= -0.5) return 'Bajo';
  if (z < 0.5) return 'Promedio';
  return 'Alto';
}

function getMiniIPIPLevelFromAvgFallback(avg: number): MiniIPIPLevel {
  if (avg < 2.75) return 'Bajo';
  if (avg <= 3.25) return 'Promedio';
  return 'Alto';
}

export interface MiniIpipNormContext {
  normUsed: string;
  nNorm: number;
  dims: Record<string, { meanAvg: number; sdAvg: number }>;
}

export function scoreMiniIPIP20WithNorm(
  rawAnswers: number[],
  norm: MiniIpipNormContext | null
): {
  answersRaw: number[];
  answersAdjusted: number[];
  dimensions: Record<string, MiniIPIPDimensionResult>;
} {
  const base = scoreMiniIPIP20(rawAnswers);
  const dimKeys = [
    'responsabilidadOrden',
    'estabilidadEmocional',
    'energiaSocial',
    'cooperacion',
    'aperturaAdaptabilidad',
  ] as const;

  const dimensions: Record<string, MiniIPIPDimensionResult> = {};
  for (const key of dimKeys) {
    const d = base.dimensions[key];
    if (!d) continue;

    let level: MiniIPIPLevel;
    let z: number | undefined;
    let normUsed: string;
    let nNorm: number;

    if (norm && norm.dims[key]) {
      const { meanAvg, sdAvg } = norm.dims[key];
      if (sdAvg > 0.05) {
        z = (d.avg - meanAvg) / sdAvg;
        level = getMiniIPIPLevelFromZ(z);
        normUsed = norm.normUsed;
        nNorm = norm.nNorm;
      } else {
        level = getMiniIPIPLevelFromAvgFallback(d.avg);
        normUsed = 'fallback';
        nNorm = 0;
      }
    } else {
      level = getMiniIPIPLevelFromAvgFallback(d.avg);
      normUsed = 'fallback';
      nNorm = 0;
    }

    dimensions[key] = {
      ...d,
      level,
      text: MINI_IPIP_TEXTS[key][level],
      z,
      normUsed,
      nNorm,
    };
  }

  return {
    answersRaw: base.answersRaw,
    answersAdjusted: base.answersAdjusted,
    dimensions,
  };
}

/** Calcula avg/percent por dimensión (sin norma). Usado por scoreMiniIPIP20WithNorm. */
export function scoreMiniIPIP20(rawAnswers: number[]): {
  answersRaw: number[];
  answersAdjusted: number[];
  dimensions: Record<string, { avg: number; percent: number }>;
} {
  const adjusted = rawAnswers.map((v, i) =>
    MINI_IPIP_INV_INDICES.has(i) ? invertMiniIPIP(v) : (typeof v === 'number' && v >= 1 && v <= 5 ? v : 3)
  );

  const dimIndices: [number, number, number, number][] = [
    [0, 5, 10, 15],
    [1, 6, 11, 16],
    [2, 7, 12, 17],
    [3, 8, 13, 18],
    [4, 9, 14, 19],
  ];
  const dimKeys = [
    'responsabilidadOrden',
    'estabilidadEmocional',
    'energiaSocial',
    'cooperacion',
    'aperturaAdaptabilidad',
  ] as const;

  const dimensions: Record<string, { avg: number; percent: number }> = {};
  for (let d = 0; d < 5; d++) {
    const sum = dimIndices[d].reduce((s, i) => s + adjusted[i], 0);
    const avg = sum / 4;
    const percent = Math.round(((avg - 1) / 4) * 100);
    dimensions[dimKeys[d]] = { avg, percent };
  }

  return {
    answersRaw: [...rawAnswers],
    answersAdjusted: [...adjusted],
    dimensions,
  };
}
